import React, { createContext, useContext, useState } from 'react';

const TutorialContext = createContext();

export function TutorialProvider({ children }) {
  // Track if user has completed the first-run tutorial
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState(() => {
    return localStorage.getItem('completedFirstRunTutorial') === 'true';
  });

  // Track if the interactive tour should run
  const [runTour, setRunTour] = useState(false);

  // Track audio player tutorial
  const [hasCompletedPlayerTutorial, setHasCompletedPlayerTutorial] = useState(() => {
    return localStorage.getItem('completedAudioPlayerTutorial') === 'true';
  });

  const [runPlayerTour, setRunPlayerTour] = useState(false);

  // Track the legacy processing tutorial
  const [showTutorial, setShowTutorial] = useState(() => {
    return localStorage.getItem('pendingTutorial') === 'true';
  });

  // Start the interactive tour (for first-time users)
  const startTour = () => {
    setRunTour(true);
  };

  // Stop the interactive tour
  const stopTour = () => {
    setRunTour(false);
  };

  // Mark tutorial as completed
  const completeTutorial = () => {
    setHasCompletedTutorial(true);
    setRunTour(false);
    localStorage.setItem('completedFirstRunTutorial', 'true');
  };

  // Reset tutorial (for testing or user request)
  const resetTutorial = () => {
    setHasCompletedTutorial(false);
    localStorage.removeItem('completedFirstRunTutorial');
  };

  // Legacy: Show processing tutorial. First submission only — repeat
  // submitters get the lightweight confirmation toast instead of a popup
  // they've already read.
  const showProcessingTutorial = () => {
    if (localStorage.getItem('processingTutorialSeen') === 'true') return;
    setShowTutorial(true);
    localStorage.setItem('pendingTutorial', 'true');
  };

  // Legacy: Hide processing tutorial
  const hideProcessingTutorial = () => {
    setShowTutorial(false);
    localStorage.removeItem('pendingTutorial');
    localStorage.setItem('processingTutorialSeen', 'true');
  };

  // Audio Player Tutorial functions
  const startPlayerTour = () => {
    setRunPlayerTour(true);
  };

  const stopPlayerTour = () => {
    setRunPlayerTour(false);
  };

  const completePlayerTutorial = () => {
    setHasCompletedPlayerTutorial(true);
    setRunPlayerTour(false);
    localStorage.setItem('completedAudioPlayerTutorial', 'true');
  };

  const resetPlayerTutorial = () => {
    setHasCompletedPlayerTutorial(false);
    localStorage.removeItem('completedAudioPlayerTutorial');
  };

  return (
    <TutorialContext.Provider
      value={{
        // Legacy processing tutorial
        showTutorial,
        showProcessingTutorial,
        hideProcessingTutorial,
        // Dashboard interactive tour
        runTour,
        startTour,
        stopTour,
        hasCompletedTutorial,
        completeTutorial,
        resetTutorial,
        // Audio Player tutorial
        runPlayerTour,
        startPlayerTour,
        stopPlayerTour,
        hasCompletedPlayerTutorial,
        completePlayerTutorial,
        resetPlayerTutorial
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}
